// # Ethics 100 Cases Component
import React, { useState, useEffect } from 'react';
import { FaBookMedical, FaVideo, FaWindowClose, FaPlay } from 'react-icons/fa';
import UnderConstruction from '../../components/UnderConstruction';
import DashboardHeader from '../../components/DashboardHeader';
import DashboardFooter from '../../components/DashboardFooter';
import './mentee_guide.css';
// # Import Firebase and cache manager
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuthStore } from '../../auth/state';
import cacheManager from '../../auth/cache_manager';

// # Import local thumbnail images
import resourcesThumbnail from '../../aneo_media/thumbnails/mentee/resources.png';
import ankiThumbnail from '../../aneo_media/thumbnails/mentee/anki.png';

function MenteeGuide({ isAdmin, stepType }) {
  const [selectedVideo, setSelectedVideo] = useState(null);
  // # Add dark theme state
  const [isDarkMode, setIsDarkMode] = useState(false);
  const { user } = useAuthStore();

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Video data for different sections
  const videoSections = [
    {
      title: "Resources",
      videos: [
        {
          title: "Top USMLE Resources",
          videoId: "cTVVF8neYkt",
          thumbnail: resourcesThumbnail,
          embedCode: '<div class="sp-embed-player" data-id="cTVVF8neYkt"><iframe width="100%" height="100%" style="border:0;" scrolling="no" src="https://go.screenpal.com/player/cTVVF8neYkt?width=100%&height=100%&ff=1&title=0" allowfullscreen="true"></iframe></div>'
        },
        // # Add more resource videos as needed
      ]
    },
    {
      title: "Note-Taking",
      videos: [
        {
          title: "How To Use Anki",
          videoId: "cTVnI8ne3w0",
          thumbnail: ankiThumbnail,
          embedCode: '<div class="sp-embed-player" data-id="cTVnI8ne3w0"><iframe width="100%" height="100%" style="border:0;" scrolling="no" src="https://go.screenpal.com/player/cTVnI8ne3w0?width=100%&height=100%&ff=1&title=0" allowfullscreen="true"></iframe></div>'
        },
        // # Add more note-taking videos as needed
      ]
    }
  ];

  // # Function to open video in modal
  const openVideoModal = (videoData) => {
    setSelectedVideo(videoData);
  };

  // # Function to close video modal
  const closeVideoModal = () => {
    setSelectedVideo(null);
  };

  // # Create a video card component
  const VideoCard = ({ video }) => (
    <div className="mentee_guide_video_card" onClick={() => openVideoModal(video)}>
      <div className="mentee_guide_video_thumbnail">
        {/* # Use local thumbnail images */}
        <img 
          src={video.thumbnail} 
          alt={video.title}
          className="mentee_guide_thumbnail_img"
        />
        <div className="mentee_guide_play_overlay">
          <FaPlay className="mentee_guide_play_icon" />
        </div>
      </div>
      <h3 className="mentee_guide_video_title">{video.title}</h3>
    </div>
  );

  return (
    <div className={`mentee_guide_container ${isDarkMode ? 'mentee_guide_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      
      <div className="mentee_guide_content">
        {/* # Title Section */}
        <div className="mentee_guide_title_box">
          <h1>Mentee Guide Videos</h1>
          <p>Essential videos to help you succeed in your medical education journey</p>
        </div>
        
        {/* # Video Sections */}
        {videoSections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="mentee_guide_section">
            <h2 className="mentee_guide_section_title">Section {sectionIndex + 1}: {section.title}</h2>
            <div className="mentee_guide_video_grid">
              {section.videos.map((video, videoIndex) => (
                <VideoCard key={videoIndex} video={video} />
              ))}
            </div>
          </div>
        ))}
      </div>
      
      {/* # Video Modal */}
      {selectedVideo && (
        <div className="mentee_guide_modal_overlay" onClick={closeVideoModal}>
          <div className="mentee_guide_modal_content" onClick={(e) => e.stopPropagation()}>
            <div className="mentee_guide_modal_header">
              <h3>{selectedVideo.title}</h3>
              <FaWindowClose className="mentee_guide_close_button" onClick={closeVideoModal} />
            </div>
            <div className="mentee_guide_modal_body" dangerouslySetInnerHTML={{ __html: selectedVideo.embedCode }} />
          </div>
        </div>
      )}
      
      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default MenteeGuide;
